import type { AppRecord, AppScore } from './types'

const COMPLEXITY_WEIGHT: Record<string, number> = {
  simple: 0.4,
  moderate: 0.65,
  complex: 0.85,
  enterprise: 1,
}

const REVENUE_WEIGHT: Record<string, number> = {
  'subscription': 1,
  'acquisition-target': 0.95,
  'one-time': 0.7,
  'open-source': 0.4,
  'internal-only': 0.3,
  'unclear': 0.5,
}

export function computeScore(app: AppRecord): AppScore {
  const now = Date.now()
  const lastMod = app.last_modified ? new Date(app.last_modified).getTime() : now - 365 * 86400e3
  const daysSince = Math.max(0, (now - lastMod) / 86400e3)

  const heat = Math.max(0, Math.round(100 * Math.exp(-daysSince / 30)))
  const trend = Math.max(0, Math.round(100 * Math.exp(-daysSince / 90)))

  const locFactor = Math.min(1, Math.log10(Math.max(100, app.loc_estimate ?? 0)) / 5)
  const cWeight = COMPLEXITY_WEIGHT[app.complexity] ?? 0.5
  const rWeight = REVENUE_WEIGHT[app.revenue_model_hint] ?? 0.5
  const stackBonus = Math.min(0.2, (app.stack?.length ?? 0) * 0.025)
  const featureBonus = Math.min(0.2, (app.key_features?.length ?? 0) * 0.04)

  const valueRaw = (locFactor * 0.3) + (cWeight * 0.35) + (rWeight * 0.35) + stackBonus + featureBonus
  const value = Math.min(100, Math.round(valueRaw * 100))

  const defensibility = Math.round(
    (cWeight * 50) +
    ((app.github_repo ? 10 : 0)) +
    (Math.min(20, (app.stack?.length ?? 0) * 2)) +
    (rWeight * 20)
  )

  const aiCommodityCategories = ['marketing-site', 'presentation-tool', 'ops-tool']
  const ai_disruption_risk = aiCommodityCategories.includes(app.category)
    ? 75
    : app.category === 'edtech' || app.category === 'healthcare-edtech'
      ? 35
      : app.category === 'creative-media-saas'
        ? 45
        : 55

  return { heat, trend, value, defensibility, ai_disruption_risk }
}

export function rankApps(apps: AppRecord[], mode: 'heat' | 'trend' | 'value' | 'defensibility'): AppRecord[] {
  return [...apps].sort((a, b) => {
    const sa = a.score ?? computeScore(a)
    const sb = b.score ?? computeScore(b)
    return (sb[mode] ?? 0) - (sa[mode] ?? 0)
  })
}

export function defaultValuationGuess(app: AppRecord) {
  const cWeight = COMPLEXITY_WEIGHT[app.complexity] ?? 0.5
  const rWeight = REVENUE_WEIGHT[app.revenue_model_hint] ?? 0.5
  const locFactor = Math.min(1, Math.log10(Math.max(100, app.loc_estimate ?? 0)) / 5)
  const base = 5000 + (locFactor * 60_000) + (cWeight * 80_000) + (rWeight * 60_000)
  return {
    acquisition_low: Math.round(base * 0.6),
    acquisition_mid: Math.round(base * 1.2),
    acquisition_high: Math.round(base * 2.4),
    one_time_low: Math.round(base * 0.25),
    one_time_mid: Math.round(base * 0.5),
    one_time_high: Math.round(base * 0.9),
    monthly_low: Math.round((base * 0.025) / 12 * 100) / 100,
    monthly_mid: Math.round((base * 0.05) / 12 * 100) / 100,
    monthly_high: Math.round((base * 0.1) / 12 * 100) / 100,
  }
}
